import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, inr, requestErrorMessage } from "@/lib/erp";
import { SDOC, TARGET_ROUTE } from "@/lib/erpdocs";
import { useActiveColumns, alignClass } from "@/components/ActiveColumns";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Plus, Search, FileText, MoreHorizontal, Eye, Pencil, Trash2, ArrowRightLeft, Ban, Printer, MessageCircle, Mail, RefreshCw } from "lucide-react";
import { shareBillWhatsApp, shareBillEmail } from "@/lib/sharebill";
import { useWhatsAppSender } from "@/context/WhatsAppSendContext";
import { toast } from "sonner";

const STATUS = {
  open: "bg-brand-100 text-brand-700 border-brand-200",
  converted: "bg-emerald-100 text-emerald-700 border-emerald-200",
  cancelled: "bg-slate-200 text-slate-500 border-slate-300",
};

export default function SalesDocs({ dtype }) {
  const requestWhatsAppSend = useWhatsAppSender();
  const cfg = SDOC[dtype];
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const requestSequence = useRef(0);
  const navigate = useNavigate();

  const cols = [
    { key: "doc_no", label: "Doc No", cell: (d) => <span className="font-mono font-medium cursor-pointer hover:text-primary" onClick={() => navigate(`${cfg.base}/${d.id}`)}>{d.doc_no}</span> },
    { key: "doc_date", label: "Date", cell: (d) => <span className="text-muted-foreground">{d.doc_date}</span> },
    { key: "customer_name", label: "Customer", cell: (d) => d.customer_name },
    { key: "grand_total", label: "Amount", align: "right", cell: (d) => <span className="font-mono">{inr(d.grand_total)}</span> },
    { key: "status", label: "Status", align: "center", cell: (d) => <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${STATUS[d.status] || STATUS.open}`}>{d.status}</span> },
  ];
  const { shown, Picker } = useActiveColumns(`sdoc_${cfg.api}`, cols);

  const load = useCallback(async () => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    try {
      const r = await http.get(`/sales-docs/${cfg.api}`, { params: { search } });
      if (sequence !== requestSequence.current) return;
      setRows(r.data);
      setLoadError("");
    } catch (e) {
      if (sequence === requestSequence.current) setLoadError(requestErrorMessage(e, `load ${cfg.plural.toLowerCase()}`));
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [search, cfg.api, cfg.plural]);
  useEffect(() => {
    const initial = setTimeout(load, 250);
    const refreshVisible = () => { if (document.visibilityState !== "hidden") load(); };
    const interval = setInterval(refreshVisible, 30000);
    window.addEventListener("focus", refreshVisible);
    document.addEventListener("visibilitychange", refreshVisible);
    return () => {
      ++requestSequence.current;
      clearTimeout(initial);
      clearInterval(interval);
      window.removeEventListener("focus", refreshVisible);
      document.removeEventListener("visibilitychange", refreshVisible);
    };
  }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await http.delete(`/sales-docs/${cfg.api}/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(requestErrorMessage(e, "delete document")); }
  };
  const cancel = async (id) => {
    try { await http.post(`/sales-docs/${cfg.api}/${id}/cancel`); toast.success("Cancelled"); await load(); }
    catch (e) { toast.error(requestErrorMessage(e, "cancel document")); }
  };
  const convert = async (id, target) => {
    try {
      const { data } = await http.post(`/sales-docs/${cfg.api}/${id}/convert`, { target });
      toast.success(`Converted to ${target.replace("_", " ")} · ${data.no}`);
      navigate(`${TARGET_ROUTE[target]}/${data.id}`);
    } catch (e) { toast.error(requestErrorMessage(e, "convert document")); }
  };

  return (
    <div id="print-area" className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{cfg.plural}</h1>
          <p className="text-sm text-muted-foreground">{loading && rows.length === 0 ? "Loading documents…" : `${rows.length} documents`}</p>
        </div>
        <Button onClick={() => navigate(`${cfg.base}/new`)} data-testid="create-sdoc-button"><Plus className="h-4 w-4 mr-1" /> New {cfg.label}</Button>
      </div>

      <Card className="p-0 overflow-hidden">
        {loadError && <div className="flex items-center justify-between gap-3 border-b border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"><span>{loadError}</span><Button size="sm" variant="outline" onClick={load}>Retry</Button></div>}
        <div className="no-print p-4 border-b flex items-center gap-3 flex-wrap">
          <div className="relative max-w-sm flex-1 min-w-[200px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="sdoc-search" />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="refresh-sdocs-button"><RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
            <Button size="sm" variant="print" onClick={() => window.print()} data-testid="print-list-button"><Printer className="h-4 w-4 mr-1" /> Print</Button>
            {Picker}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                {shown.map((c) => <th key={c.key} className={`px-4 py-3 font-medium ${alignClass(c.align)}`}>{c.label}</th>)}
                <th className="px-4 py-3 font-medium text-right no-print">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} className="border-t hover:bg-slate-50" data-testid={`sdoc-row-${d.id}`}>
                  {shown.map((c) => <td key={c.key} className={`px-4 py-3 ${alignClass(c.align)}`}>{c.cell(d)}</td>)}
                  <td className="px-4 py-3 text-right no-print">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><button className="p-1.5 hover:bg-slate-100 rounded" data-testid={`sdoc-actions-${d.id}`}><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`${cfg.base}/${d.id}`)}><Eye className="h-4 w-4 mr-2" /> View</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`${cfg.base}/${d.id}/edit`)}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => shareBillWhatsApp(`/sales-docs/${cfg.api}/${d.id}`, { phoneKey: "customer_phone", emailKey: "customer_email", nameKey: "customer_name", noKey: "doc_no", totalKey: "grand_total", label: cfg.label }, requestWhatsAppSend)} data-testid={`wa-${d.id}`}><MessageCircle className="h-4 w-4 mr-2 text-emerald-600" /> Send WhatsApp</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => shareBillEmail(`/sales-docs/${cfg.api}/${d.id}`, { phoneKey: "customer_phone", emailKey: "customer_email", nameKey: "customer_name", noKey: "doc_no", totalKey: "grand_total", label: cfg.label })} data-testid={`email-${d.id}`}><Mail className="h-4 w-4 mr-2 text-brand-600" /> Send Email</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {cfg.convert.map(([t, label]) => (
                          <DropdownMenuItem key={t} onClick={() => convert(d.id, t)}><ArrowRightLeft className="h-4 w-4 mr-2" /> {label}</DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => cancel(d.id)}><Ban className="h-4 w-4 mr-2" /> Cancel</DropdownMenuItem>
                        <DropdownMenuItem className="text-rose-600" onClick={() => del(d.id)}><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={shown.length + 1} className="px-4 py-12 text-center text-muted-foreground"><FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />{loading ? "Loading documents…" : loadError ? "Documents could not be loaded. Use Retry above." : `No ${cfg.plural.toLowerCase()}`}</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
